/**
 * Display helpers for portal/admin user records (shared by list + detail pages).
 */

export function formatUserFullName(
  user: { first_name?: string | null; last_name?: string | null } | null | undefined
): string {
  const first = user?.first_name ? String(user.first_name).trim() : ''
  const last = user?.last_name ? String(user.last_name).trim() : ''
  const name = `${first} ${last}`.trim()
  return name || '—'
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

/**
 * Display label for a user's report cadence, e.g. "Weekly (Mon)".
 */
export function formatReportCadence(
  user: { report_cadence?: string | null; report_day_of_week?: number | null } | null | undefined
): string {
  const cadence = user?.report_cadence ?? 'weekly'
  if (cadence === 'daily') return 'Daily'

  const dayOfWeek = user?.report_day_of_week ?? 1
  const dayName = DAY_NAMES[dayOfWeek] ?? DAY_NAMES[1]
  const dayAbbr = dayName.slice(0, 3)
  if (cadence === 'biweekly') return `Biweekly (${dayAbbr})`
  return `Weekly (${dayAbbr})`
}

export function formatReportingMode(
  user: { reporting_mode?: string | null } | null | undefined
): 'Net' | 'Gross' {
  return user?.reporting_mode === 'net' ? 'Net' : 'Gross'
}

export function userStatusBadgeVariant(
  status: string | undefined | null
): 'default' | 'success' | 'warning' | 'secondary' {
  if (status === 'active') return 'success'
  if (status === 'invited') return 'warning'
  if (status === 'disabled' || status === 'inactive') return 'secondary'
  return 'default'
}

/**
 * Map an invite (or similar) API `user` payload to the row shape returned by GET /admin/users.
 */
export function listUserRowFromInvitePayload(
  user: Record<string, unknown> | null | undefined
): Record<string, unknown> | null {
  if (!user || typeof user !== 'object' || user.id == null) return null
  const propertyIds = Array.isArray(user.property_ids) ? user.property_ids : []
  const lastPortal = (user.last_portal_login_at ?? user.last_login_at ?? null) as unknown
  return {
    id: user.id,
    email: user.email,
    first_name: user.first_name ?? null,
    last_name: user.last_name ?? null,
    role: user.role,
    status: user.status,
    property_count: propertyIds.length,
    auth0_user_id: user.auth0_user_id ?? null,
    invited_at: user.invited_at ?? null,
    invite_sent_at: user.invite_sent_at ?? null,
    last_login_at: lastPortal,
    last_portal_login_at: lastPortal,
    notify_new_reports_published: Boolean(user.notify_new_reports_published),
    report_cadence: user.report_cadence ?? 'weekly',
    report_day_of_week: user.report_day_of_week ?? 1,
    reporting_mode: user.reporting_mode ?? 'gross',
    created_at: user.created_at,
    updated_at: user.updated_at,
  }
}
